import {
  Box,
  IconButton,
  Typography,
  Grid,
  useTheme,
  Skeleton
} from '@mui/material';
import { AppConfig } from 'AppConfig';
import { confirmDialog } from 'common/ComfirmationDialog';
import { useMediaQuery } from 'react-responsive';
import * as ReportActions from 'features/frontoffice/actions/actions';
import * as ReportTypes from 'features/frontoffice/actions/types';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { store } from 'store/store';
import { formatReptSource } from 'utils/formatReptSource';
import { trackClick, trackingActions, trackingTypes } from 'utils/tracking';
import { hasPermission } from 'utils/common';

import DollarIcon from '../../icons/DollarIcon';
import ReportOperation from '../../features/landing/newHome/ReportOperation';
import SmartCardQuickViewDrawer from './SmartCardQuickViewDrawer';
import CardTitle from './cardTitle';
import {
  getWorstBenchmark,
  formatPEPM,
  getStatusColor,
} from '../../features/landing/newHome/smartCardHelpers';
import { useState, useRef, useMemo, useEffect } from 'react';
import { useShowToolTip } from 'common/hooks/useShowToolTip';
import * as Actions from '../../features/frontoffice/actions/actions'
import { showRestoreToast } from 'utils/showRestoreToast';
import * as Constant from 'common/Constant';

const KpiSlot = ({ loading, width = 64, height = 20, children }) =>
  loading ? <Skeleton variant="text" width={width} height={height} animation="wave" /> : children;

const SmartCard = ({
  item,
  showDismiss = false,
  showFavorite = true,
  reportType,
  kpiLoading = false,
  setShowSmartCard,
  setSelectedSmartCardId,
  hiddenCount,
  viewMode = 'grid',
  onUnhideStart,
  onUnhideSettled,
  onUnhideError
}) => {
  const isMobil = useMediaQuery({ query: '(min-width: 320px) and (max-width: 767px)' });
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const permissions = useSelector((state) => state.user.permissions);
  const aiSummaryAvailable = useSelector((state) => state.user.aiSummaryAvailable);
  const tableauServiceUrl = useSelector((state) => state.user.tableauServiceUrl);
  const theme = useTheme();
  const [imageError, setImageError] = useState(false);
  const [isBlankThumbnail, setIsBlankThumbnail] = useState(false);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const isPlaceholder = item?.isPlaceholder;
  const showKpiSkeleton = kpiLoading || isPlaceholder;

  const hasQuickView = false;

  const titleRef = useRef();
  const isTitleOverflowing = useShowToolTip(titleRef);

  const navigate = useNavigate();
  const location = useLocation();

  const shouldShowFavorite = showFavorite

  const handleReportClick = () => {   
    if(isPlaceholder || !item?.reptSource) return; 
    if (item?.id) {
      trackClick(
        trackingTypes.REPORT,
        trackingActions.READ,
        item.id,
        item.category + ';' + item.displayText
      );
    }
    let reportOriginComponent = Constant.TOP_SMART_CARDS;
    // Determine reportBaseUrl based on current route
    let reportBaseUrl = '/bi'; // Default for Home tab

    if (location.pathname.includes('/reports') || location.pathname.includes('/hidden-smart-card')) {
      reportBaseUrl = '/bi/reports';
      reportOriginComponent = Constant.REPORTS;
    } else if (location.pathname.includes('/smart-cards')) {
      reportBaseUrl = '/bi/smart-cards';
    }

    const [workbook, dashboard] = item.reptSource.split('/');
    navigate(`${'/bi'}/${currentTenantCd}/view/${workbook}/${dashboard}`);

    store.dispatch({
      type: ReportTypes.OPEN_REPORT_VIEWER_REQUEST,
      reportUrl: item.reportSource
        ? item.reportSource
        : formatReptSource(tableauServiceUrl, currentTenantCd, item.reptSource),
      category: item.category,
      title: item.displayText,
      reportBaseUrl: reportBaseUrl,
      report: item,
      reportOriginComponent
    });
    sessionStorage.setItem('reportOrigin', JSON.stringify({ reportBaseUrl, reportOriginComponent }));
  };

  const handleUnHiddenSmartCard = () => {
    if (!item) return;
    onUnhideStart?.();
    const isLastCardHidden = hiddenCount === 1;

    store.dispatch(Actions.unhiddenReport([item.id])).then(() => {
        onUnhideSettled?.();
        if(!isLastCardHidden) {
            showRestoreToast("Smart Card Restored", "250px", "142px", "#005C2C", false);
        }
      })
      .catch((error) => {
        onUnhideError?.();
        onUnhideSettled?.();
        showRestoreToast('Could not restore Smart Card(s).', '320px', '220px', '#88041E', true);
      });
  };

  const handleShareClick = () => {
       alert("shared clicked for smartCard");
  };

  const dismiss = () => {
    if (isMobil) {
      confirmDialog(
        () => store.dispatch(ReportActions.dismissReport(item.id)),
        'If you dismiss this Smart Card, it will be removed from your view. You can recover it later from <b>Smart Card Recovery</b> on the <b>Smart Cards page</b>.',
        'Dismiss Smart Card',
        'Cancel',
        'Confirm',
        undefined,
        true // isMobil flag
      );
    } else {
      confirmDialog(
        () => store.dispatch(ReportActions.dismissReport(item.id)),
        'Are you sure you want to dismiss this report?',
        'Dismiss Smart Card'
      );
    }
  };

  const truncateText = (text = '', max) => {
    const str = String(text || '');
    if (str.length <= max) return str;
    return `${str.slice(0, max - 3).trimEnd()}...`;
  };

  const getCardTitle = () => {
    return item.displayText || item.description;
  };

  const handleQuickViewOpen = () => {
    setIsQuickViewOpen(true);
  };

  const handleQuickViewClose = () => {
    setIsQuickViewOpen(false);
  };

  const handleQuickViewLaunch = () => {
    setIsQuickViewOpen(false);
    handleReportClick();
  };

  const handleEyeClick = () => {
    if (location.pathname === "/bi/hidden-smart-card") {
      handleUnHiddenSmartCard();
      return;
    }
    setShowSmartCard?.(true);
    setSelectedSmartCardId?.({ id: item.id, title: getCardTitle() });
  };

  const hasThumbnail = Boolean(item?.s3Url && item.s3Url.trim() !== '');
  const showPreviewUnavailable = !hasThumbnail || imageError || isBlankThumbnail;

  // ---------------------------------------------------------------------------
  // Derive display values from API-supplied item fields.
  //
  // All five fields below are populated by the backend via the Tableau
  // SmartcardData/SCDataVisualization view and are serialised into the
  // /user/fo-detail response as part of each SmartCardDetailView object.
  //
  //   item.pmpm             – Per-Member-Per-Month cost (raw integer, e.g. 52
  //                           for $52 PMPM).  Formatted for display by formatPEPM().
  //   item.costTrend        – "Better" | "Worse" | "On Par" text from Tableau.
  //                           Passed directly to getWorstBenchmark() so the
  //                           Benchmark Trend badge reflects the Tableau value.
  //   item.estimatedSavings – Raw savings amount in dollars.  Only used when
  //                           item.quartile is absent (fallback path).
  //   item.reachImpactPct   – % of members impacted (e.g. 23.7 means 23.7%)
  //                           (e.g. 18.2 for 18.2%).
  //   item.quartile         – Pre-computed savings quartile 1–4 from Tableau.
  //                           Drives the $ / $$ / $$$ / $$$$ indicator directly,
  //                           avoiding a client-side quartile re-calculation.
  // ---------------------------------------------------------------------------

  /**
   * Pick the worse-performing benchmark dimension (Cost vs Utilization).
   * When costTrend text from Tableau is available it takes precedence over
   * the numeric benchmarkCostPct deviation for the Cost dimension.
   */
  const benchmark = useMemo(
    () => getWorstBenchmark(item.benchmarkCostPct, item.benchmarkUtilPct, item.costTrend),
    [item.benchmarkCostPct, item.benchmarkUtilPct, item.costTrend]
  );
  const statusColor = useMemo(() => getStatusColor(benchmark.status), [benchmark]);

  /**
   * Financial impact indicator string ('$' through '$$$$').
   * Uses the Tableau-supplied quartile directly when present
   * (index range 0–3 s) falls back to '$' when absent.
   */
  const financialImpact = useMemo(() => {
    if (item.quartile != null) {
      return ['$', '$$', '$$$', '$$$$'][Math.max(0, Math.min(item.quartile - 1, 3))];
    }
    return '$';
  }, [item.quartile]);

  if (viewMode === 'list') {
    return (
      <>
        <Box
          sx={{
            width: '100%',
            maxWidth: '1584px',
            minWidth: 0,
            border: '1px solid #71767A',
            borderRadius: '16px',
            p: '24px',
            bgcolor: '#FFF',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            overflow: 'hidden'
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
            <CardTitle
              card={item}
              showEye
              titleRef={titleRef}
              isTitleOverflowing={isTitleOverflowing}
              onEyeClick={handleEyeClick}
              containerSx={{ flex: '1 1 auto', minWidth: 0, alignItems: 'center' }}
              titleSx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#000',
                fontFamily: 'Rubik',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '150%',
                letterSpacing: '0.16px'
              }}
              eyeContainerSx={{ alignSelf: 'center', flexDirection: 'row' }}
            />
            <Box sx={{ flex: '0 0 auto' }}>
              <ReportOperation
                card={item}
                handleReportClick={handleReportClick}
                handleShareClick={handleShareClick}
                shouldShowFavorite={shouldShowFavorite}
                hasQuickView={hasQuickView}
                handleQuickViewClick={handleQuickViewOpen}
                containerPx={0}
                compact
                disabled={isPlaceholder}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '40px',
              width: '100%',
              minWidth: 0
            }}>
            <Box
              sx={{
                width: 'clamp(220px, 24vw, 300px)',
                height: '100px',
                flex: '0 0 clamp(220px, 24vw, 300px)',
                p: '24px',
                borderRadius: '16px',
                bgcolor: '#F8F8F8',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                boxSizing: 'border-box'
              }}>
              <Typography
                sx={{
                  width: '100%',
                  color: '#121A22',
                  textAlign: 'center',
                  fontFamily: 'Rubik',
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: '140%',
                  letterSpacing: '0.36px',
                  textTransform: 'uppercase'
                }}>
                PMPM
              </Typography>
              <Typography
                sx={{
                  color: '#000',
                  fontFamily: 'Rubik',
                  fontSize: '28px',
                  fontWeight: 600,
                  lineHeight: '144%',
                  letterSpacing: '0.28px',
                  whiteSpace: 'nowrap'
                }}>
                <KpiSlot loading={showKpiSkeleton} width={96} height={40}>
                {item.pmpm != null ? formatPEPM(item.pmpm) : 'N/A'}
                </KpiSlot>
              </Typography>
            </Box>

            <Box
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(140px, 1fr))',
                gap: '24px',
                alignItems: 'stretch'
              }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 }}>
                <Typography sx={{ color: '#121A22', fontFamily: 'Noto Sans', fontSize: '14px', fontWeight: 400, lineHeight: '150%' }}>
                  Benchmark Trend ({benchmark.label})
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <KpiSlot loading={showKpiSkeleton} width={56} height={24}>
                  {benchmark.status === 'N/A' ? (
                    <Typography sx={{ color: '#121A22', fontFamily: '"Noto Sans"', fontSize: '14px', fontWeight: 600, lineHeight: '150%', whiteSpace: 'nowrap' }}>
                      N/A
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        height: '24px',
                        px: '8px',
                        py: '6px',
                        alignItems: 'center',
                        borderRadius: '8px',
                        bgcolor: statusColor,
                        boxSizing: 'border-box'
                      }}>
                      <Typography sx={{ color: '#FFF', fontFamily: 'Rubik', fontSize: '12px', fontWeight: 500, lineHeight: 1.4, letterSpacing: '0.36px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                        {benchmark.status}
                      </Typography>
                    </Box>
                  )}
                  </KpiSlot>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
                <Typography sx={{ color: '#121A22', fontFamily: 'Noto Sans', fontSize: '14px', fontWeight: 400, lineHeight: '150%' }}>
                  Reach / Impact
                </Typography>
                <Typography sx={{ color: '#121A22', fontFamily: 'Noto Sans', fontSize: '14px', fontWeight: 600, lineHeight: '150%', whiteSpace: 'nowrap' }}>
                  <KpiSlot loading={showKpiSkeleton} width={90}>
                    {item.reachImpactPct != null ? `${Number(item.reachImpactPct).toFixed(1)}%` : '0%'} of members
                  </KpiSlot>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
                <Typography sx={{ color: '#121A22', fontFamily: 'Noto Sans', fontSize: '14px', fontWeight: 400, lineHeight: '150%' }}>
                  Estimated Financial Impact
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <KpiSlot loading={showKpiSkeleton} width={80}>
                  {[1, 2, 3, 4].map((level) => (
                    <DollarIcon key={level} active={level <= financialImpact.length} />
                  ))}
                  </KpiSlot>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <SmartCardQuickViewDrawer
          open={isQuickViewOpen}
          onClose={handleQuickViewClose}
          card={item}
          onLaunch={handleQuickViewLaunch}
        />
      </>
    );
  }

  // ---- Smart Card layout (Figma design) ----
  return (
    <>
      {/* ===== CARD TOP (Figma) ===== */}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          padding: '24px 24px 16px 24px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '16px',
          borderRadius: '16px 16px 0 0',
          borderTop: '1px solid #71767A',
          borderRight: '1px solid #71767A',
          borderLeft: '1px solid #71767A',
          borderBottom: 'none',
          background: '#FFF',
        }}
      >
        {/* ===== TITLE ROW + EYE ICON ===== */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px',
            alignSelf: 'stretch',
          }}
        >
           {isPlaceholder ? (
          <Skeleton variant="text" width="70%" height={28} animation="wave" sx={{ flex: '1 0 0' }}/> 
          ) : (
          <CardTitle
            card={item}
            showEye
            titleRef={titleRef}
            isTitleOverflowing={isTitleOverflowing}
            onEyeClick={() => {
                handleEyeClick();
            }}
            titleSx={{
              height: '48px',
              flex: '1 0 0',
              color: '#000',
              wordBreak: 'break-word',
              fontFamily: 'Rubik',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '150%',
              letterSpacing: '0.16px',
            }}
          />
          )}
        </Box>

        {/* ===== PMPM SECTION ===== */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '16px',
            alignSelf: 'stretch',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              padding: '24px',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              alignSelf: 'stretch',
              borderRadius: '16px',
              background: '#F8F8F8',
            }}
          >
            {/* PMPM Label */}
            <Typography
              sx={{
                alignSelf: 'stretch',
                color: '#121A22',
                textAlign: 'center',
                fontFamily: 'Rubik',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '140%',
                letterSpacing: '0.36px',
                textTransform: 'uppercase',
              }}
            >
              pmpm
            </Typography>
            {/* PMPM Value */}
            <Typography
              sx={{
                color: '#000',
                fontFamily: 'Rubik',
                fontSize: '28px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '144%',
                letterSpacing: '0.28px',
              }}
            >
              <KpiSlot loading={showKpiSkeleton} width={96} height={40}>
                {item.pmpm != null ? formatPEPM(item.pmpm) : 'N/A'}
              </KpiSlot>
            </Typography>
          </Box>
        </Box>

        {/* ===== BENCHMARK TREND + STATUS BADGE (inline row) ===== */}
        <Box
          sx={{
            display: 'flex',
            height: '32px',
            alignItems: 'center',
            gap: '8px',
            alignSelf: 'stretch',
          }}
        >
          {/* Benchmark Trend Label */}
          <Typography
            sx={{
              flex: '1 0 0',
              color: '#121A22',
              fontFamily: '"Noto Sans"',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '150%',
            }}
          >
            Benchmark Trend ({benchmark.label})
          </Typography>

          {/* Status Badge / N/A free text */}
           <KpiSlot loading={showKpiSkeleton} width={56} height={24}>
          {benchmark.status === 'N/A' ? (
            <Typography
              sx={{
                color: '#121A22',
                fontFamily: '"Noto Sans"',
                fontSize: '14px',
                fontWeight: 600,
                lineHeight: '150%',
                whiteSpace: 'nowrap',
              }}
            >
              N/A
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'flex',
                height: '24px',
                padding: '6px 8px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '4px',
                borderRadius: '8px',
                background: statusColor,
              }}
            >
              <Typography
                sx={{
                  color: '#FFF',
                  fontFamily: 'Rubik',
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: 1.4,
                  letterSpacing: '0.36px',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                {benchmark.status}
              </Typography>
            </Box>
          )}
          </KpiSlot>
        </Box>

        {/* ===== CONTENT AREA BELOW BENCHMARK ===== */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '24px',
            alignSelf: 'stretch',
          }}
        >
          {/* Reach / Impact */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              alignSelf: 'stretch',
            }}
          >
            <Typography
              sx={{
                display: 'flex',
                height: '32px',
                flexDirection: 'column',
                justifyContent: 'center',
                flex: '1 0 0',
                color: '#121A22',
                fontFamily: '"Noto Sans"',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '150%',
              }}
            >
              Reach / Impact
            </Typography>
            <Typography
              sx={{
                color: '#121A22',
                fontFamily: '"Noto Sans"',
                fontSize: '14px',
                fontWeight: 600,
                lineHeight: '150%',
                whiteSpace: 'nowrap',
              }}
            >
              <KpiSlot loading={showKpiSkeleton} width={90}>
                {item.reachImpactPct != null ? `${Number(item.reachImpactPct).toFixed(1)}%` : '0%'} of members
              </KpiSlot>
            </Typography>
          </Box>

          {/* Estimated Financial Impact */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '24px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                alignSelf: 'stretch',
              }}
            >
              <Typography
                sx={{
                  display: 'flex',
                  height: '32px',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  flex: '1 0 0',
                  color: '#121A22',
                  fontFamily: '"Noto Sans"',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '150%',
                }}
              >
                Estimated Financial Impact
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  alignSelf: 'stretch',
                }}
              >
                <KpiSlot loading={showKpiSkeleton} width={80}>
                  {[1, 2, 3, 4].map((level) => (
                    <DollarIcon key={level} active={level <= financialImpact.length} />
                  ))}
                </KpiSlot>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ===== ACTION ROW BOTTOM (Figma) ===== */}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          minHeight: '76px',
          padding: '24px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '10px',
          borderRadius: '0 0 16px 16px',
          borderRight: '1px solid #71767A',
          borderBottom: '1px solid #71767A',
          borderLeft: '1px solid #71767A',
          borderTop: 'none',
          background: '#FFF',
        }}
      >
        <ReportOperation
          card={item}
          handleReportClick={handleReportClick}
          handleShareClick={handleShareClick}
          shouldShowFavorite={shouldShowFavorite}
          hasQuickView={hasQuickView}
          handleQuickViewClick={handleQuickViewOpen}
          containerPx={0}
          leftActionGap="12px"
          disabled={isPlaceholder}
        />
      </Box>

      <SmartCardQuickViewDrawer
        open={isQuickViewOpen}
        onClose={handleQuickViewClose}
        card={item}
        onLaunch={handleQuickViewLaunch}
      />
    </>
  );
};

export default SmartCard;